import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import toaster from 'react-hot-toast';
import AutocompleteDropdown from 'components/common/autocompleteDropdown/AutocompleteDropdown';
import { useBusinessSearch } from 'hooks/useBusinessSearch';
import { getIndividualBusiness } from 'api/admin/businesses/businesses';

interface BusinessOption {
  value: number;
  display: string;
}

interface BusinessPickerProps {
  selectedBusinessId?: number | null;
  onChange: (businessId: number | null) => void;
  id?: string;
  label?: string;
  size?: 'small' | 'medium';
  disabled?: boolean;
}

export const BusinessPicker: React.FC<BusinessPickerProps> = ({
  selectedBusinessId = null,
  onChange,
  id = 'business-picker',
  label = 'Business',
  size = 'medium',
  disabled = false,
}) => {
  const { businessOptions, isLoading, error, onInputChange, filterOptions } = useBusinessSearch();

  // When the selected business isn't in the current search results (e.g. navigating back via the
  // browser with a URL-persisted businessId that was only visible after a specific search), fetch
  // it by ID so the picker can display the correct label without requiring a re-search.
  const isSelectedInResults =
    selectedBusinessId != null && businessOptions.some((o) => o.value === selectedBusinessId);

  const { data: fetchedBusiness } = useQuery({
    queryKey: ['businessPickerById', selectedBusinessId],
    queryFn: async () => {
      if (selectedBusinessId == null) return null;
      const { data } = await getIndividualBusiness(selectedBusinessId);
      const { id, name } = data.business as { id: number; name: string };
      return { value: id, display: `${name} [ID: ${id}]` } as BusinessOption;
    },
    enabled: selectedBusinessId != null && !isSelectedInResults,
    staleTime: 5 * 60 * 1000,
  });

  // Merge the individually-fetched business into the options list so MUI Autocomplete can
  // match it to the controlled value and render the label correctly.
  const effectiveOptions = useMemo(() => {
    if (fetchedBusiness && !isSelectedInResults) {
      return [fetchedBusiness, ...businessOptions];
    }
    return businessOptions;
  }, [fetchedBusiness, businessOptions, isSelectedInResults]);

  // Cache the resolved option for the current selection. Once resolved for a given
  // selectedBusinessId, deliberately keep the same object reference stable — updating it on
  // every effectiveOptions change (i.e. every keystroke after debounce) would give MUI
  // Autocomplete a new `value` reference each render, causing it to reset inputValue and
  // overwrite whatever the user is typing.
  const [resolvedOption, setResolvedOption] = useState<BusinessOption | null>(null);

  useEffect(() => {
    if (selectedBusinessId == null) {
      setResolvedOption(null);
      return;
    }
    // Already resolved for this business — keep the reference stable.
    if (resolvedOption?.value === selectedBusinessId) return;
    const match = effectiveOptions.find((o) => o.value === selectedBusinessId);
    if (match) {
      setResolvedOption(match);
    } else if (fetchedBusiness?.value === selectedBusinessId) {
      setResolvedOption(fetchedBusiness);
    }
    // Else: still loading — leave resolvedOption as-is (null on first mount).
    // resolvedOption intentionally omitted from deps: once set for the current selectedBusinessId
    // it must not be replaced by a fresh object reference from a new search result page.
  }, [selectedBusinessId, effectiveOptions, fetchedBusiness]);

  useEffect(() => {
    if (error) {
      toaster.error(error);
    }
  }, [error]);

  return (
    <AutocompleteDropdown
      id={id}
      selectedData={resolvedOption}
      onChange={(option) => onChange((option?.value as number) ?? null)}
      label={label}
      selectData={effectiveOptions}
      size={size}
      disabled={disabled}
      onInputChange={onInputChange}
      filterOptions={filterOptions}
      loading={isLoading}
    />
  );
};
